import { useState, useEffect } from 'react';
import { PlusIcon, TrashIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { getApiUrl } from '../../utils/api';

interface MonitoredRegion {
  id: number;
  provider: string;
  region: string;
  display_name: string;
  enabled: boolean;
  is_default: boolean;
  latitude?: number;
  longitude?: number;
  quota_summary?: string;
}

interface OfferedRegion {
  provider: string;
  region: string;
  instance_types?: string[];
  max_instances_per_user: number;
  pricing_multiplier: number;
}

interface AvailableRegion {
  region: string;
  display_name: string;
  latitude?: number;
  longitude?: number;
  is_monitored: boolean;
}

interface QuotaPreview {
  instance_family: string;
  quota_limit: number;
  current_usage: number;
  available: number;
  gpu_type: string;
}

export default function RegionManager() {
  const [monitoredRegions, setMonitoredRegions] = useState<{ [key: string]: MonitoredRegion[] }>({});
  const [offeredRegions, setOfferedRegions] = useState<{ [key: string]: OfferedRegion[] }>({});
  const [availableRegions, setAvailableRegions] = useState<{ [key: string]: AvailableRegion[] }>({});
  const [availableRegionsLoading, setAvailableRegionsLoading] = useState(false);
  const [quotaPreview, setQuotaPreview] = useState<QuotaPreview[] | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addingRegion, setAddingRegion] = useState(false);
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState('aws');
  const [selectedRegion, setSelectedRegion] = useState<AvailableRegion | null>(null);

  useEffect(() => {
    fetchRegions();
  }, []);

  const fetchRegions = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [monitored, offered] = await Promise.all([
        axios.get(`${getApiUrl()}/api/quotas/regions/monitored`, { headers }),
        axios.get(`${getApiUrl()}/api/quotas/regions/offered`, { headers })
      ]);

      setMonitoredRegions(monitored.data.regions || {});
      setOfferedRegions(offered.data.regions || {});
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch regions:', err);
      setError('Failed to load region configuration');
      setLoading(false);
    }
  };

  const fetchAvailableRegions = async () => {
    try {
      setAvailableRegionsLoading(true);
      const token = localStorage.getItem('authToken');
      const headers = { 'Authorization': `Bearer ${token}` };

      const response = await axios.get(`${getApiUrl()}/api/quotas/regions/available`, { headers });
      setAvailableRegions(response.data.providers || {});
    } catch (err) {
      console.error('Failed to fetch available regions:', err);
      setError('Failed to load available regions');
    } finally {
      setAvailableRegionsLoading(false);
    }
  };

  const fetchQuotaPreview = async (provider: string, region: string) => {
    try {
      setPreviewLoading(true);
      const token = localStorage.getItem('authToken');
      const headers = { 'Authorization': `Bearer ${token}` };

      const response = await axios.get(
        `${getApiUrl()}/api/quotas/regions/preview/${provider}/${region}`,
        { headers }
      );
      setQuotaPreview(response.data.quotas || []);
    } catch (err) {
      console.error('Failed to fetch quota preview:', err);
      setQuotaPreview([]);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleRegionSelect = async (region: AvailableRegion) => {
    setSelectedRegion(region);
    if (region && selectedProvider) {
      await fetchQuotaPreview(selectedProvider, region.region);
    }
  };

  const handleAddRegion = async () => {
    if (!selectedRegion) {
      setValidationMessage('Please select a region to add');
      return;
    }

    if (selectedRegion.is_monitored) {
      setValidationMessage('This region is already being monitored');
      return;
    }

    try {
      setAddingRegion(true);
      setValidationMessage('Adding region to monitoring...');
      setError(null);

      const token = localStorage.getItem('authToken');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Add to monitored regions
      await axios.post(`${getApiUrl()}/api/quotas/regions/monitored`, {
        provider: selectedProvider,
        region: selectedRegion.region,
        display_name: selectedRegion.display_name,
        latitude: selectedRegion.latitude,
        longitude: selectedRegion.longitude
      }, { headers });

      setValidationMessage('Region added! Configuring for user offerings...');

      // Also add to offered regions by default
      await axios.post(`${getApiUrl()}/api/quotas/regions/offered`, {
        provider: selectedProvider,
        region: selectedRegion.region,
        enabled: true
      }, { headers });

      setValidationMessage('Success! Region is now being monitored and offered to users.');

      // Wait a moment to show success message
      setTimeout(() => {
        setShowAddForm(false);
        setSelectedRegion(null);
        setQuotaPreview(null);
        setValidationMessage(null);
        fetchRegions();
        fetchAvailableRegions();
      }, 2000);

    } catch (err: any) {
      console.error('Failed to add region:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to add region. Please try again.';
      setError(errorMessage);
      setValidationMessage(null);
    } finally {
      setAddingRegion(false);
    }
  };

  const handleRemoveRegion = async (provider: string, region: string) => {
    if (!window.confirm(`Remove ${provider}/${region} from monitoring?`)) return;

    try {
      const token = localStorage.getItem('authToken');
      const headers = { 'Authorization': `Bearer ${token}` };

      await axios.delete(`${getApiUrl()}/api/quotas/regions/monitored/${provider}/${region}`, { headers });
      fetchRegions();
    } catch (err) {
      console.error('Failed to remove region:', err);
      setError('Failed to remove region');
    }
  };

  const toggleOfferedRegion = async (provider: string, region: string, currentlyOffered: boolean) => {
    try {
      const token = localStorage.getItem('authToken');
      const headers = { 'Authorization': `Bearer ${token}` };

      await axios.post(`${getApiUrl()}/api/quotas/regions/offered`, {
        provider,
        region,
        enabled: !currentlyOffered
      }, { headers });

      fetchRegions();
    } catch (err) {
      console.error('Failed to toggle offered region:', err);
      setError('Failed to update region offering');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const providers = ['aws', 'gcp', 'azure'];
  const providerColors: Record<string, string> = {
    aws: 'bg-orange-900/20 border-orange-800',
    gcp: 'bg-blue-900/20 border-blue-800',
    azure: 'bg-sky-900/20 border-sky-800',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Region Configuration</h2>
          <p className="text-gray-400 mt-1">Manage monitored regions and user offerings</p>
        </div>
        <button
          onClick={async () => {
            setShowAddForm(true);
            setError(null);
            setValidationMessage(null);
            if (Object.keys(availableRegions).length === 0) {
              await fetchAvailableRegions();
            }
          }}
          className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Add Region
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 text-red-400">
          {error}
        </div>
      )}

      {/* Add Region Form */}
      {showAddForm && (
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Add New Region</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">Provider</label>
              <select
                value={selectedProvider}
                onChange={(e) => {
                  setSelectedProvider(e.target.value);
                  setSelectedRegion(null);
                  setQuotaPreview(null);
                }}
                className="w-full px-3 py-2 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none"
              >
                {providers.filter(p => p === 'aws' || p === 'gcp').map(p => (
                  <option key={p} value={p}>{p.toUpperCase()}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">Region</label>
              {availableRegionsLoading ? (
                <div className="w-full px-3 py-2 bg-gray-800 text-gray-400 rounded-lg border border-gray-700">
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                    Loading regions...
                  </div>
                </div>
              ) : (
                <select
                  value={selectedRegion?.region || ''}
                  onChange={(e) => {
                    const region = availableRegions[selectedProvider]?.find(r => r.region === e.target.value);
                    if (region) {
                      setValidationMessage(null);
                      handleRegionSelect(region);
                    }
                  }}
                  className="w-full px-3 py-2 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none"
                  disabled={!availableRegions[selectedProvider] || availableRegions[selectedProvider].length === 0}
                >
                  <option value="">Select a region...</option>
                  {availableRegions[selectedProvider]?.map(region => (
                    <option
                      key={region.region}
                      value={region.region}
                      disabled={region.is_monitored}
                      className={region.is_monitored ? 'text-gray-500' : ''}
                    >
                      {region.display_name} ({region.region}) {region.is_monitored ? '✓ Already monitored' : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* Validation Message */}
          {validationMessage && (
            <div className={`mt-4 p-3 rounded-lg ${
              validationMessage.includes('Success') ? 'bg-green-900/20 border border-green-800 text-green-400' :
              validationMessage.includes('Adding') || validationMessage.includes('Configuring') ? 'bg-blue-900/20 border border-blue-800 text-blue-400' :
              'bg-yellow-900/20 border border-yellow-800 text-yellow-400'
            }`}>
              <p className="text-sm">{validationMessage}</p>
            </div>
          )}

          {/* Quota Preview Section */}
          {selectedRegion && !selectedRegion.is_monitored && (
            <div className="mt-6">
              <h4 className="text-md font-semibold text-white mb-3">GPU Quotas for {selectedRegion.display_name}</h4>
              {previewLoading ? (
                <div className="flex items-center justify-center p-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  <span className="ml-2 text-gray-400">Fetching quota data...</span>
                </div>
              ) : quotaPreview && quotaPreview.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {quotaPreview.map(quota => (
                    <div key={quota.instance_family} className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-white font-medium">{quota.instance_family}</p>
                          <p className="text-xs text-gray-400">{quota.gpu_type}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-green-400 font-semibold">{quota.quota_limit} GPUs</p>
                          <p className="text-xs text-gray-400">{quota.available} available</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="col-span-full bg-blue-900/20 rounded-lg p-3 border border-blue-800">
                    <p className="text-blue-400 text-sm">
                      Total GPU Capacity: <span className="font-semibold">
                        {quotaPreview.reduce((sum, q) => sum + q.quota_limit, 0)} GPUs
                      </span>
                    </p>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                  <p className="text-gray-400 text-sm">No GPU quotas available for this region</p>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => {
                setShowAddForm(false);
                setSelectedRegion(null);
                setQuotaPreview(null);
              }}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAddRegion}
              disabled={!selectedRegion || addingRegion || selectedRegion?.is_monitored}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {addingRegion ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Adding...
                </>
              ) : (
                'Add Region'
              )}
            </button>
          </div>
        </div>
      )}

      {/* Region List by Provider */}
      {providers.map(provider => {
        const providerMonitored = monitoredRegions[provider] || [];
        const providerOffered = offeredRegions[provider] || [];
        const offeredRegionSet = new Set(providerOffered.map(r => r.region));

        if (providerMonitored.length === 0) return null;

        return (
          <div key={provider} className={`rounded-lg border p-6 ${providerColors[provider]}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white flex items-center">
                <GlobeAltIcon className="w-6 h-6 mr-2" />
                {provider.toUpperCase()} Regions
              </h3>
              <span className="text-sm text-gray-400">
                {providerMonitored.length} monitored, {providerOffered.length} offered
              </span>
            </div>

            <div className="space-y-3">
              {providerMonitored.map(region => {
                const isOffered = offeredRegionSet.has(region.region);

                return (
                  <div key={region.id} className="flex items-center justify-between bg-gray-900 rounded-lg p-4">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <span className="text-white font-medium">{region.display_name}</span>
                        <span className="ml-3 text-gray-500 text-sm">({region.region})</span>
                        {region.is_default && (
                          <span className="ml-2 px-2 py-0.5 bg-gray-700 text-gray-400 text-xs rounded">
                            Default
                          </span>
                        )}
                        {isOffered && (
                          <span className="ml-2 px-2 py-0.5 bg-green-900/50 text-green-400 text-xs rounded">
                            ✓ Offered
                          </span>
                        )}
                      </div>
                      {region.quota_summary && (
                        <p className="text-xs text-gray-400 mt-1">
                          GPU Quotas: {region.quota_summary}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center space-x-3">
                      <div className="flex items-center">
                        <label className="text-sm text-gray-400 mr-2">Offer to users:</label>
                        <button
                          onClick={() => toggleOfferedRegion(provider, region.region, isOffered)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            isOffered ? 'bg-blue-600' : 'bg-gray-700'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              isOffered ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      {!region.is_default && (
                        <button
                          onClick={() => handleRemoveRegion(provider, region.region)}
                          className="p-2 text-red-400 hover:bg-red-900/20 rounded-lg transition-colors"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}